import { useEffect, useMemo, useState } from 'preact/hooks';
import { bootOnce } from '../../bootstrap';
import { navigate, requestAppBack } from '../../router/router';
import { IconBack, IconChat, IconClose, IconStar, IconReceipt, IconRefresh } from '../../ui/icons';
import { useI18n } from '../../bootstrap/i18n';
import { useGiftsCount } from '../gifts/GiftsRoute';
import { trackPageView } from '../../analytics';
import { translateCommandText } from '../../chat/player/scenario';
import { getTelegramId, getTelegramUser, type TelegramUser } from '../../utils/telegramUser';
import { fetchJson, getAccessTokenOrNull } from '../../utils/api';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { fetchPublicAsset } from '../../utils/publicFetch';

type ProfileData = {
  telegram: TelegramUser;
  credits: number;
  subscription?: {
    tier: string;
    is_premium: boolean;
    is_billionaire?: boolean;
    expires_at?: string | null;
    days_remaining?: number;
    plan?: { id?: number; name?: string; duration_days?: number | null } | null;
  } | null;
};

type CreditTx = {
  id?: number;
  type?: string | null;
  amount?: number | null;
  balance_after?: number | null;
  created_at?: string | null;
  description?: string | null;
  reference_id?: number | null;
  reference_type?: string | null;
};

type SessionRow = {
  id: number;
  model_id: number;
  started_at?: string | null;
  last_activity?: string | null;
  is_active?: boolean | null;
  models?: { id: number; name: string; image_url?: string | null } | null;
};

function normalizeLocale2(raw: string | null | undefined): string {
  const v = String(raw || '').trim().toLowerCase();
  const base = v.split(/[-_]/)[0];
  const clean = base.replace(/[^a-z]/g, '');
  return clean || 'en';
}

function titleCaseFirst(s: string): string {
  const v = String(s || '');
  if (!v) return '';
  return v.slice(0, 1).toUpperCase() + v.slice(1);
}

function getNativeLanguageName(code: string): string {
  const loc = normalizeLocale2(code);
  try {
    const dn = new Intl.DisplayNames([loc], { type: 'language' });
    const v = dn.of(loc) || loc;
    // UI-ish: match prior UX with leading uppercase where applicable
    return titleCaseFirst(v);
  } catch {
    return loc.toUpperCase();
  }
}

function GiftsBadge() {
  const { count } = useGiftsCount();
  if (count <= 0) return null;
  return (
    <span
      style={{
        position: 'absolute',
        top: -4,
        right: -4,
        background: '#DC143C',
        color: '#fff',
        borderRadius: 9,
        minWidth: 18,
        height: 18,
        fontSize: 10,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 4px',
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function ProfileRoute(props: { onOpenTopup?: () => void; isPremium?: boolean | null } = {}) {
  const services = bootOnce();
  const { t, locale: currentLocale } = useI18n(services.i18n);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<ProfileData | null>(null);
  const [txOpen, setTxOpen] = useState(false);
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [transactions, setTransactions] = useState<CreditTx[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [selectedLang, setSelectedLang] = useState<string>(() => currentLocale || 'en');
  const [availableLocales, setAvailableLocales] = useState<string[]>([]);
  useEffect(() => {
    setSelectedLang(currentLocale || 'en');
  }, [currentLocale]);

  // Load locales manifest for language selector (no hardcoded list).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchPublicAsset(`/locales/manifest.json?t=${Date.now()}`, undefined, { expected_content_type: 'json', feature: 'profile', stage: 'locales_manifest' });
        if (!r.ok) return;
        const j = await r.json();
        const list0 = Array.isArray(j?.locales) ? j.locales : (Array.isArray(j?.namespaces?.ui) ? j.namespaces.ui : []);
        const list = (list0 || [])
          .map((x: any) => normalizeLocale2(String(x || '')))
          .filter((x: string) => /^[a-z]{2}$/.test(x));
        // Deterministic order for UX
        list.sort((a: string, b: string) => a.localeCompare(b));
        if (!cancelled) setAvailableLocales(list);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);

      const telegram = getTelegramUser();
      if (!telegram?.id) {
        setErr(reportHandledError({
          err: new Error('telegram_user_missing'),
          kind: 'auth',
          code: 'telegram.user_missing',
          userMessage: t('telegram.user_missing', 'Telegram user not available'),
          severity: 'warning',
          context: { feature: 'profile' },
        }));
        setLoading(false);
        return;
      }

      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
      if (!accessToken) {
        setErr(reportHandledError({
          err: new Error('auth_not_ready'),
          kind: 'auth',
          code: 'auth.not_ready',
          userMessage: t('auth.not_ready', 'Auth not ready yet'),
          severity: 'warning',
          context: { feature: 'profile' },
        }));
        setLoading(false);
        return;
      }

      if (cancelled) return;
      
      // Fetch balance and subscription in parallel
      const [bal, sub] = await Promise.all([
        fetchJson<{ ok: boolean; credits_balance: number }>('/api/user/balance', {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetchJson<{ ok: boolean; subscription: any }>('/api/user/subscription', {
        headers: { Authorization: `Bearer ${accessToken}` },
        }).catch(() => ({ ok: false, status: 0, data: null } as any)),
      ]);
      
      if (!bal.ok) {
        setErr(reportHandledError({
          err: new Error(`api_error:${bal.status || 0}`),
          kind: 'api',
          code: 'profile.load_failed',
          userMessage: t('error.loading', 'Error loading data'),
          severity: 'error',
          context: { feature: 'profile', endpoint: '/api/user/balance', status: bal.status, resp: (bal as any).data },
        }));
        setLoading(false);
        return;
      }

      setData({ 
        telegram, 
        credits: Number((bal.data as any)?.credits_balance ?? 0),
        // fetchJson wraps server JSON into { ok, data }, so subscription lives at sub.data.subscription
        subscription: (sub as any)?.ok ? (((sub as any)?.data as any)?.subscription || null) : null,
      });
      setLoading(false);

      // Analytics: track profile page view
      trackPageView('profile');
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep balance in sync when credits are changed elsewhere (tasks/purchases).
  useEffect(() => {
    const onRefresh = async () => {
      try {
        const telegramId = getTelegramId();
        if (!telegramId) return;
        const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
        if (!accessToken) return;
        const r = await fetchJson<{ ok: boolean; credits_balance: number }>('/api/user/balance', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!r.ok) return;
        const nextCredits = Number((r.data as any)?.credits_balance ?? 0);
        setData((prev) => (prev ? { ...prev, credits: nextCredits } : prev));
      } catch {}
    };
    window.addEventListener('aicams-credits-refresh', onRefresh as any);
    return () => window.removeEventListener('aicams-credits-refresh', onRefresh as any);
  }, [services]);

  const displayName = useMemo(() => {
    if (!data?.telegram) return t('profile.user_fallback', 'User');
    const tg = data.telegram;
    return tg.first_name || tg.username || t('profile.user_fallback', 'User');
  }, [data, t]);

  const displayUsername = useMemo(() => {
    const u = data?.telegram?.username;
    return u ? `@${u}` : '';
  }, [data]);

  async function loadTransactions() {
    if (!data?.telegram?.id) return;
    setTxLoading(true);
    try {
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
      if (!accessToken) {
        setErr(reportHandledError({
          err: new Error('auth_not_ready'),
          kind: 'auth',
          code: 'auth.not_ready',
          userMessage: t('auth.not_ready', 'Auth not ready yet'),
          severity: 'warning',
          context: { feature: 'profile', action: 'load_transactions' },
        }));
        return;
      }
      const r = await fetchJson<{ ok: boolean; data: any[] }>(`/api/user/transactions?limit=50`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error('load_transactions_failed');
      setTransactions((((r.data as any)?.data) || []) as any[]);
    } catch (e: any) {
      setErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'profile.load_transactions_failed',
        userMessage: t('error.loading', 'Error loading data'),
        severity: 'error',
        context: { feature: 'profile', action: 'load_transactions' },
      }));
    } finally {
      setTxLoading(false);
    }
  }

  async function loadSessions() {
    if (!data?.telegram?.id) return;
    setSessionsLoading(true);
    try {
      const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
      if (!accessToken) {
        setErr(reportHandledError({
          err: new Error('auth_not_ready'),
          kind: 'auth',
          code: 'auth.not_ready',
          userMessage: t('auth.not_ready', 'Auth not ready yet'),
          severity: 'warning',
          context: { feature: 'profile', action: 'load_sessions' },
        }));
        return;
      }
      const r = await fetchJson<{ ok: boolean; data: any[] }>(`/api/user/sessions?limit=50`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error('load_sessions_failed');
      setSessions((((r.data as any)?.data) || []) as any[]);
    } catch (e: any) {
      setErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'profile.load_sessions_failed',
        userMessage: t('error.loading', 'Error loading data'),
        severity: 'error',
        context: { feature: 'profile', action: 'load_sessions' },
      }));
    } finally {
      setSessionsLoading(false);
    }
  }

  async function persistLanguage(lang: string) {
    try {
      const telegramId = getTelegramId();
      if (telegramId) {
        const accessToken = await getAccessTokenOrNull(services, { timeoutMs: 3000 });
        if (!accessToken) return;
        await fetchJson('/api/user/language', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ lang }),
        });
      }
    } catch {}
  }

  return (
    <div>
      <div class="row" style={{ marginBottom: 10 }}>
        <button class="iconBtn" onClick={() => requestAppBack()} aria-label={t('btn.back', 'Back')}>
          <IconBack />
        </button>
        <div style={{ fontWeight: 900 }}>{t('nav.profile', 'Profile')}</div>
        <div />
      </div>

      {loading && <div class="muted" style={{ padding: 12 }}>{t('profile.loading', 'Loading profile...')}</div>}
      {err && (
        <div class="card" style={{ padding: 12, borderColor: 'rgba(255,90,95,0.35)' }}>
          <div style={{ color: 'rgba(255,90,95,0.95)', fontWeight: 800 }}>{err}</div>
          <div style={{ marginTop: 10 }}>
            <button class="iconBtn" onClick={() => window.location.reload()}>
              <IconRefresh />
            </button>
          </div>
        </div>
      )}

      {data && (
        <div style={{ display: 'grid', gap: 12 }}>
          <div class="card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 999,
                background: 'linear-gradient(135deg, rgba(43,124,255,0.45), rgba(43,124,255,0.15))',
                border: '1px solid rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
                fontWeight: 900,
              }}
            >
              {data.telegram.photo_url ? (
                <img src={data.telegram.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span>{displayName.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                {displayName}
                {(() => {
                  // Billionaire has highest priority
                  if (data.subscription?.is_billionaire || data.subscription?.tier === 'billionaire') {
                    return <span class="billionaire-user-badge">👑👑👑</span>;
                  } else if (data.subscription?.tier === 'premium' || data.subscription?.is_premium) {
                    return <span class="premium-user-badge">{t('user.premium', 'PREMIUM')}</span>;
                  } else {
                    return <span class="free-user-badge">{t('user.free', 'FREE')}</span>;
                  }
                })()}
              </div>
              <div class="muted" style={{ fontSize: 12 }}>{displayUsername || `Telegram ID: ${data.telegram.id}`}</div>
            </div>
          </div>

          {/* Premium Status Card */}
          {data.subscription?.is_premium && (
            <div class="card premium-status-card" style={{ padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 800, color: '#FFD700' }}>{t('profile.premium_active', '✨ Premium Active')}</div>
                  <div class="muted" style={{ fontSize: 12 }}>
                    {(() => {
                      const exp = data.subscription?.expires_at ? new Date(String(data.subscription.expires_at)) : null;
                      const hasExp = exp && Number.isFinite(exp.getTime());
                      if (hasExp) {
                        let dateText = '';
                        try {
                          dateText = new Intl.DateTimeFormat([currentLocale || 'en'], {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                          }).format(exp as Date);
                        } catch {
                          dateText = (exp as Date).toLocaleDateString();
                        }
                        return t('profile.premium_until', 'Premium until {date}').replace('{date}', dateText);
                      }
                      return (data.subscription.days_remaining && data.subscription.days_remaining > 0)
                        ? t('profile.days_remaining', '{days} days remaining').replace('{days}', String(data.subscription.days_remaining))
                        : t('profile.premium_active_desc', 'Unlimited messages & all features');
                    })()}
                  </div>
                  {data.subscription?.plan?.name && (
                    <div class="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {data.subscription.plan.name}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div class="card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>{t('profile.your_balance', 'Your Balance')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 900, fontSize: 30 }}>
              <span>{data.credits.toLocaleString()}</span>
              <IconStar class="starSvg" size={26} filled />
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
              <button class="primaryBtn" style={{ flex: 1 }} onClick={() => (props.onOpenTopup ? props.onOpenTopup() : null)}>
                {t('nav.topup', 'Top Up')}
              </button>
              <button class="secondaryBtn" style={{ flex: 1 }} onClick={() => navigate({ name: 'tasks', from: 'profile' })}>
                {t('nav.tasks', 'Tasks')}
              </button>
              <button class="secondaryBtn" style={{ flex: 1, position: 'relative' }} onClick={() => navigate({ name: 'gifts' })}>
                🎁 {t('nav.gifts', 'Gifts')}
                <GiftsBadge />
              </button>
            </div>
          </div>

          <div class="card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>{t('profile.history', 'History')}</div>
            <div style={{ display: 'grid', gap: 8 }}>
              <button
                class="listBtn"
                onClick={async () => {
                  setTxOpen(true);
                  await loadTransactions();
                }}
              >
                <IconReceipt />
                <span style={{ fontWeight: 900 }}>{t('profile.transaction_history', 'Transaction History')}</span>
              </button>
              <button
                class="listBtn"
                onClick={async () => {
                  setChatHistoryOpen(true);
                  await loadSessions();
                }}
              >
                <IconChat />
                <span style={{ fontWeight: 900 }}>{t('profile.chat_history', 'Chat History')}</span>
              </button>
            </div>
          </div>

          {/* Language (inline, no extra click) */}
          <div class="card" style={{ padding: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>{t('profile.language', 'Language')}</div>
            <select
              class="selectInput"
              value={selectedLang}
              onChange={(e) => {
                const v = (e.currentTarget as HTMLSelectElement).value;
                setSelectedLang(v);
                
                // 1. Apply immediately to UI (instant)
                try { services.i18n.setLocale(v); } catch {}
                
                // 2. Persist to DB (async)
                void persistLanguage(v);
              }}
            >
              {(() => {
                const cur = normalizeLocale2(selectedLang || currentLocale || 'en');
                const list = Array.isArray(availableLocales) && availableLocales.length ? availableLocales : [cur];
                const uniq = Array.from(new Set([cur, ...list])).filter((x) => /^[a-z]{2}$/.test(x));
                uniq.sort((a, b) => a.localeCompare(b));
                return uniq.map((code) => (
                  <option key={code} value={code}>{getNativeLanguageName(code)}</option>
                ));
              })()}
            </select>
          </div>
        </div>
      )}

      {/* Transaction History Modal */}
      {txOpen && (
        <div class="modalOverlay">
          <div class="modalScreen">
            <div class="modalHeader">
              <div class="modalTitle">{t('profile.transaction_history', 'Transaction History')}</div>
              <button class="iconBtn" onClick={() => setTxOpen(false)} aria-label={t('btn.close', 'Close')}>
                <IconClose />
              </button>
            </div>
            <div class="modalBody">
              {txLoading && <div class="muted">{t('common.loading', 'Loading')}</div>}
              {!txLoading && transactions.length === 0 && <div class="muted">{t('profile.no_history', 'No transaction history yet')}</div>}
              {!txLoading && transactions.map((tx) => {
                const amount = Number(tx.amount || 0);
                const positive = amount > 0;
                const when = tx.created_at ? new Date(tx.created_at).toLocaleString() : '';
                return (
                  <div class="card" style={{ padding: 12, borderLeft: `4px solid ${positive ? 'rgba(39,209,127,0.8)' : 'rgba(255,90,95,0.8)'}` }}>
                    <div class="row" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 900 }}>{t(`tx.${tx.type}`, tx.type || 'transaction')}</div>
                        <div class="muted" style={{ fontSize: 12, marginTop: 4 }}>{when}</div>
                        {tx.description && <div class="muted" style={{ fontSize: 12, marginTop: 6 }}>
                          {tx.type === 'command_spent' 
                            ? translateCommandText(tx.description, t, currentLocale)
                            : tx.type === 'task_reward' && tx.reference_id
                              ? t(`task.${tx.reference_id}.title`, tx.description)
                              : tx.description}
                        </div>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 900, color: positive ? 'rgba(39,209,127,0.95)' : 'rgba(255,90,95,0.95)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span>{positive ? '+' : ''}{amount}</span>
                          <IconStar class="starSvg" size={18} filled />
                        </div>
                        <div class="muted" style={{ fontSize: 11, marginTop: 2 }}>→ {tx.balance_after ?? '?'}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Chat History Modal */}
      {chatHistoryOpen && (
        <div class="modalOverlay">
          <div class="modalScreen">
            <div class="modalHeader">
              <div class="modalTitle">{t('profile.chat_history', 'Chat History')}</div>
              <button class="iconBtn" onClick={() => setChatHistoryOpen(false)} aria-label={t('btn.close', 'Close')}>
                <IconClose />
              </button>
            </div>
            <div class="modalBody">
              {sessionsLoading && <div class="muted">{t('common.loading', 'Loading')}</div>}
              {!sessionsLoading && sessions.length === 0 && <div class="muted">{t('profile.no_chats', 'No chat history')}</div>}
              {!sessionsLoading && sessions.map((s) => {
                const model = s.models || null;
                const name = model?.name || `Model ${s.model_id}`;
                const when = s.last_activity ? new Date(s.last_activity).toLocaleString() : (s.started_at ? new Date(s.started_at).toLocaleString() : '');
                return (
                  <div
                    class="card"
                    style={{ padding: 12, cursor: 'pointer' }}
                    onClick={() => {
                      setChatHistoryOpen(false);
                      navigate({ name: 'chat', modelId: String(model?.id || s.model_id) });
                    }}
                  >
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <div style={{ width: 46, height: 46, borderRadius: 999, overflow: 'hidden', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--stroke)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>
                        {model?.image_url ? <img src={model.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span>{name.slice(0, 1).toUpperCase()}</span>}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                        <div class="muted" style={{ fontSize: 12 }}>
                          {when}{s.is_active ? ` • ${t('profile.active', 'Active')}` : ''}
                        </div>
                      </div>
                      <div class="muted">›</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
